import { useCallback } from "react";
import Link from "next/link";
import BlendAddButton from "domains/models/components/Blend/BlendCreation/BlendAddButton";
import { useCreateBlendContext } from "domains/models/contexts/CreateBlendProvider";
import { getModelThumbnail } from "domains/models/utils";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Icon from "domains/ui/components/Icon";
import {
  GetModelsByModelIdApiResponse,
  GetModelsDescriptionByModelIdApiResponse,
} from "infra/api/generated/api";

import { Flex, Image, Text } from "@chakra-ui/react";

export default function Model({
  data,
  imageWidth,
  withAddToCompositionButton,
}: {
  data?:
    | GetModelsDescriptionByModelIdApiResponse["description"]["models"][0]
    | GetModelsByModelIdApiResponse["model"];
  imageWidth?: string;
  withAddToCompositionButton?: boolean;
}) {
  const router = useRouter();
  const { selectedTeam, selectedProject } = useTeamContext();
  const { addConcept, removeConcept } = useCreateBlendContext();

  const handleAdd = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!data) return;
      addConcept(data.id);
    },
    [addConcept, data]
  );

  const handleUndoAdd = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!data) return;
      removeConcept(data.id);
    },
    [removeConcept, data]
  );

  return (
    <Link
      href={
        router.pathname.includes("/compose/")
          ? {
              pathname: "/compose/new",
              query: {
                selectedModelId: data?.id,
                teamId: selectedTeam.id,
                projectId: selectedProject.id,
              },
            }
          : {
              pathname: `/models/[id]`,
              query: {
                id: data?.id ?? "",
                teamId: selectedTeam.id,
                projectId: selectedProject.id,
              },
            }
      }
    >
      <Flex
        pos="relative"
        direction="column"
        w={imageWidth ?? "full"}
        _hover={{
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.25)",
          filter: "brightness(0.9)",
        }}
      >
        <Image
          w={imageWidth ?? "100%"}
          h="200px"
          objectFit={"cover"}
          alt="model thumbnail"
          roundedTop="lg"
          src={
            getModelThumbnail(data, {
              height: 256,
            }).url
          }
        />

        <Flex
          align="center"
          justify="space-evenly"
          p={2}
          bg="backgroundSecondary.500"
          roundedBottom="lg"
        >
          <Text
            align="center"
            maxW="200px"
            mx="auto"
            py={2}
            isTruncated
            size="body.bold.sm"
          >
            {data?.name}
          </Text>
          <Icon id="Ui/Link" color="textPrimary" />
        </Flex>

        {withAddToCompositionButton && (
          <BlendAddButton onAdd={handleAdd} onUndoAdd={handleUndoAdd} />
        )}
      </Flex>
    </Link>
  );
}
